/**
 * Share project (LED-1151, `home-projects.dc.html` "Share project") — a
 * Notion-style popover anchored under the scoped Home header's Share button.
 *
 * Sharing controls only; export and auxiliary links are currently hidden.
 *
 * SHARE is wired to the console's real roster APIs, the same ones the Invite
 * page uses: `GET /api/invite` for the people list and `POST /api/invitations`
 * to invite. Access in Led AI is ORGANIZATION-wide — an invited member sees
 * every project (`grantsEveryProject`) — so inviting from a project invites
 * to the team, and the popover says so rather than implying per-project ACLs.
 * The role dropdown maps onto the two roles that exist: Full access = Admin,
 * Can edit = Member (`PATCH /api/members/:id`, admins only). Can view is drawn
 * and disabled: there is no view-only tier yet.
 *
 * A press anywhere outside closes it, as does Escape.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchInvite,
  patchMutation,
  postMutation,
  type InviteResponse,
  type OrgMemberRow,
  type OrgRole,
} from "../../api";
import { useResource } from "../../useResource";
import { copyToClipboard } from "../../lib/clipboard";
import { FONT_MONO, H } from "../../tokens";

type ShareRole = "full" | "edit" | "view";

const ROLE_LABEL: Record<ShareRole, string> = {
  full: "Full access",
  edit: "Can edit",
  view: "Can view",
};

export function shareRoleOf(role: OrgRole): ShareRole {
  return role === "admin" ? "full" : "edit";
}

/** Initials for an avatar: first letters of the first two words. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return (words[0]![0]! + (words[1]?.[0] ?? "")).toUpperCase();
}

function domainOf(email?: string | null): string | null {
  const at = email?.lastIndexOf("@") ?? -1;
  return at > 0 ? email!.slice(at + 1).toLowerCase() : null;
}

/** A Guest is anyone whose email is not on the caller's company domain. */
export function isGuest(email: string | undefined, companyDomain: string | null): boolean {
  const domain = domainOf(email);
  return !!domain && !!companyDomain && domain !== companyDomain;
}

/** "a@x.com, b@y.com" → the addresses, and whatever was not one. */
export function parseInvitees(raw: string): { emails: string[]; rejected: string[] } {
  const parts = raw.split(/[,;\s]+/).map((part) => part.trim()).filter(Boolean);
  const emails: string[] = [];
  const rejected: string[] = [];
  for (const part of parts) {
    if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(part)) {
      if (!emails.includes(part.toLowerCase())) emails.push(part.toLowerCase());
    } else rejected.push(part);
  }
  return { emails, rejected };
}

function Glyph({ d, size = 13, color = H.text, width = 1.9 }: { d: string; size?: number; color?: string; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ display: "block", flexShrink: 0 }}>
      {d.split("|").map((part) => <path key={part} d={part} />)}
    </svg>
  );
}

const G = {
  caret: "m6 9 6 6 6-6",
  person: "M12 11.5a3.8 3.8 0 1 0 0-7.6 3.8 3.8 0 0 0 0 7.6Z|M4.5 20.5c0-4 3.4-6.5 7.5-6.5s7.5 2.5 7.5 6.5",
  send: "M21 3 10 14|M21 3l-7 18-4-7-7-4 18-7Z",
  check: "M20 6 9 17l-5-5",
  chevron: "m9 6 6 6-6 6",
};

function RoleMenu({
  value,
  editable,
  busy,
  onChange,
  testId,
}: {
  value: ShareRole;
  editable: boolean;
  busy?: boolean;
  onChange: (next: ShareRole) => void;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <span ref={wrap} style={{ position: "relative", flex: "none" }}>
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={!editable || busy}
        title={editable ? undefined : "Only an admin can change roles"}
        onClick={() => setOpen((next) => !next)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, border: 0, background: "transparent", padding: "4px 2px", font: "inherit", fontSize: 14, color: H.text, cursor: editable ? "pointer" : "default", whiteSpace: "nowrap", opacity: busy ? 0.5 : 1 }}
      >
        {ROLE_LABEL[value]}
        <Glyph d={G.caret} size={12} color={H.muted} width={2} />
      </button>
      {open && (
        <div role="menu" className="pop" style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 5, width: 200, padding: 4, borderRadius: 10, background: H.white, border: `1px solid ${H.edgeStrong}`, boxShadow: `0 12px 30px ${H.popShadow}` }}>
          {(["full", "edit", "view"] as const).map((role) => (
            <button
              key={role}
              type="button"
              role="menuitemradio"
              aria-checked={value === role}
              disabled={role === "view"}
              data-testid={`role-option-${role}`}
              className="pickrow"
              onClick={() => {
                setOpen(false);
                if (role !== value) onChange(role);
              }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: 0, borderRadius: 7, background: "transparent", font: "inherit", fontSize: 13.5, color: role === "view" ? H.faint : H.ink, cursor: role === "view" ? "not-allowed" : "pointer", textAlign: "left" }}
            >
              <span style={{ flex: 1 }}>
                {ROLE_LABEL[role]}
                {role === "view" && <span style={{ display: "block", fontSize: 11.5, color: H.faint }}>No view-only access yet</span>}
              </span>
              {value === role && <Glyph d={G.check} size={12} color={H.ink} width={2.2} />}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function Avatar({ name, color, size = 38 }: { name: string; color: string; size?: number }) {
  return (
    <span style={{ flex: "none", width: size, height: size, borderRadius: size / 2, background: color, color: H.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: size > 30 ? 12 : 7.5, fontWeight: 800 }}>
      {initialsOf(name).slice(0, size > 30 ? 1 : 2)}
    </span>
  );
}

/** The header's stacked avatars beside Share — who can see this project. */
export function ShareAvatars({ members, ring }: { members: OrgMemberRow[]; ring: string }) {
  if (members.length === 0) return null;
  return (
    <span data-testid="share-avatars" style={{ display: "flex", alignItems: "center", marginRight: 2, paddingLeft: 7 }}>
      {members.slice(0, 4).map((member, index) => (
        <span
          key={member.userId}
          title={member.name}
          style={{ width: 22, height: 22, borderRadius: 11, background: H.avatars[index % H.avatars.length], color: H.white, border: `2px solid ${ring}`, marginLeft: -7, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 7.5, fontWeight: 800, boxSizing: "content-box" }}
        >
          {initialsOf(member.name)}
        </span>
      ))}
    </span>
  );
}

export function useShareRoster(search: string, refreshKey: number, enabled: boolean) {
  const [mutations, setMutations] = useState(0);
  // The popover's own state lives HERE, with the roster, not in the popover:
  // a roster write purges the console's cache (`postMutation`), Home briefly
  // re-renders its loading shape, and a popover that owned its draft and
  // "Invited …" confirmation would lose them on the way back.
  const [draft, setDraft] = useState("");
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const resource = useResource<InviteResponse>(
    enabled ? `invite:${search}` : null,
    () => fetchInvite(search),
    refreshKey + mutations,
    { keepPreviousDataOnNavigation: true },
  );
  return {
    ...resource,
    bump: () => setMutations((count) => count + 1),
    ui: { draft, setDraft, message, setMessage },
  };
}

export function SharePopover({
  anchor,
  projectName,
  search,
  roster,
  callerEmail,
  self,
  shareUrl,
  onClose,
}: {
  /** The reader, drawn as the only person when the workspace has no roster
   *  (Personal). */
  self?: OrgMemberRow;
  anchor: React.RefObject<HTMLElement | null>;
  projectName: string;
  /** Workspace-only query — what every roster call carries. */
  search: string;
  roster: ReturnType<typeof useShareRoster>;
  callerEmail?: string;
  shareUrl: string;
  onClose: () => void;
}) {
  const { draft, setDraft, message, setMessage } = roster.ui;
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const width = 560;

  const place = () => {
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect) return { top: 120, left: 200 };
    // Under the Share button when it fits; otherwise lifted just enough that
    // the footer (Copy link) stays on screen — the design itself
    // floats it up over the greeting at a 900px-tall window.
    const height = panel.current?.offsetHeight ?? 0;
    return {
      top: Math.max(16, Math.min(rect.bottom + 10, window.innerHeight - height - 16)),
      left: Math.max(8, Math.min(rect.right - width + 40, window.innerWidth - width - 16)),
    };
  };
  const [box, setBox] = useState(place);
  useLayoutEffect(() => {
    const update = () => setBox(place());
    update();
    addEventListener("resize", update);
    addEventListener("scroll", update, true);
    return () => {
      removeEventListener("resize", update);
      removeEventListener("scroll", update, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster.data?.members?.length, message?.text]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panel.current?.contains(target) || anchor.current?.contains(target)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchor, onClose]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1400);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const data = roster.data;
  const members = data?.members?.length ? data.members : self && data ? [self] : [];
  const companyDomain = domainOf(callerEmail) ?? domainOf(members.find((member) => member.userId === data?.userId)?.email);
  const canInvite = !!data?.canInvite;

  const invite = async () => {
    const { emails, rejected } = parseInvitees(draft);
    if (rejected.length) {
      setMessage({ ok: false, text: `Use an email address to invite someone new: ${rejected.join(", ")}` });
      return;
    }
    if (emails.length === 0) {
      setMessage({ ok: false, text: "Add at least one email address first." });
      return;
    }
    setSending(true);
    setMessage(null);
    const result = await postMutation("/api/invitations", search, { emails, role: "member" });
    setSending(false);
    if (!result.ok) {
      setMessage({ ok: false, text: result.error ?? `The invitation was refused (${result.status}).` });
      return;
    }
    setDraft("");
    setMessage({ ok: true, text: `Invited ${emails.length === 1 ? emails[0] : `${emails.length} people`} — they will see ${projectName} and the team’s other projects.` });
    roster.bump();
  };

  const changeRole = async (member: OrgMemberRow, next: ShareRole) => {
    if (next === "view") return;
    setBusyId(member.userId);
    setMessage(null);
    const result = await patchMutation(`/api/members/${encodeURIComponent(member.userId)}`, search, {
      role: next === "full" ? "admin" : "member",
    });
    setBusyId(null);
    if (!result.ok) {
      setMessage({ ok: false, text: result.error ?? `That role change was refused (${result.status}).` });
      return;
    }
    roster.bump();
  };

  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-label={`Share ${projectName}`}
      data-testid="share-popover"
      className="pop"
      style={{ position: "fixed", top: box.top, left: box.left, width, zIndex: 60, background: H.white, border: `1px solid ${H.edge}`, borderRadius: 14, boxShadow: `0 24px 60px ${H.popShadow}`, overflow: "hidden", color: H.ink, textAlign: "left" }}
    >
      <div style={{ padding: "18px", borderBottom: `1px solid ${H.edge}` }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Share</h2>
      </div>
      <div style={{ padding: "16px 18px 0" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            autoFocus
            data-testid="share-invite-input"
            aria-label="Email or name, separated by commas"
            placeholder="Email or name, separated by commas"
            value={draft}
            disabled={!canInvite || sending}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void invite();
              }
            }}
            style={{ flex: 1, minWidth: 0, height: 42, padding: "0 13px", borderRadius: 9, border: `2px solid ${H.ink}`, background: H.white, font: "inherit", fontSize: 14, color: H.ink, outline: "none", boxSizing: "border-box" }}
          />
          <button
            type="button"
            className="press"
            data-testid="share-invite"
            disabled={!canInvite || sending}
            onClick={() => void invite()}
            style={{ display: "inline-flex", alignItems: "center", height: 42, padding: "0 16px", borderRadius: 9, border: 0, background: H.ink, color: H.white, font: "inherit", fontSize: 14, fontWeight: 700, cursor: canInvite ? "pointer" : "not-allowed", opacity: canInvite ? 1 : 0.5 }}
          >
            {sending ? "Inviting…" : "Invite"}
          </button>
        </div>
        {(message || (data && !canInvite)) && (
          <div data-testid="share-message" role="status" style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.45, color: message ? (message.ok ? H.greenInk : H.redInk) : H.sub }}>
            {message?.text ?? data?.unavailableReason ?? data?.readOnlyReason ?? "You can’t invite people to this workspace."}
          </div>
        )}
        <div style={{ marginTop: 8 }} data-testid="share-people">
          {roster.loading && !data && <div style={{ padding: "14px 0", fontSize: 13, color: H.muted }}>Loading people…</div>}
          {members.map((member, index) => {
            const you = member.userId === data?.userId || member === self;
            const guest = !you && isGuest(member.email, companyDomain);
            return (
              <div key={member.userId} data-testid="share-person" style={{ display: "flex", alignItems: "center", gap: 12, height: 56 }}>
                <Avatar name={member.name || member.email || "?"} color={H.avatars[index % H.avatars.length]!} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 14.5, fontWeight: 600 }}>
                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{member.name || member.email}</span>
                    {you && member.name !== "You" && <span style={{ color: H.faint, fontWeight: 500 }}>(You)</span>}
                    {guest && <span data-testid="share-guest" style={{ fontSize: 11.5, fontWeight: 600, color: H.amberInk, background: H.amberSoft, padding: "2px 7px", borderRadius: 4 }}>Guest</span>}
                  </div>
                  {member.email && <div style={{ fontSize: 13, color: H.muted, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{member.email}</div>}
                </div>
                <RoleMenu
                  testId="share-role"
                  value={shareRoleOf(member.role)}
                  editable={!!data?.canManage && !you}
                  busy={busyId === member.userId}
                  onChange={(next) => void changeRole(member, next)}
                />
              </div>
            );
          })}
          {(data?.pending ?? []).map((invitation, index) => (
            <div key={invitation.id} data-testid="share-pending" style={{ display: "flex", alignItems: "center", gap: 12, height: 56 }}>
              <Avatar name={invitation.email} color={H.avatars[(members.length + index) % H.avatars.length]!} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 14.5, fontWeight: 600 }}>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{invitation.email}</span>
                  {isGuest(invitation.email, companyDomain) && <span style={{ fontSize: 11.5, fontWeight: 600, color: H.amberInk, background: H.amberSoft, padding: "2px 7px", borderRadius: 4 }}>Guest</span>}
                </div>
                <div style={{ fontSize: 13, color: H.muted, marginTop: 1 }}>Invited · waiting to join</div>
              </div>
              <span style={{ fontSize: 14, color: H.faint, whiteSpace: "nowrap" }}>{ROLE_LABEL[shareRoleOf(invitation.role)]}</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 14, color: H.sub, marginTop: 14 }}>General access</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
          <span style={{ width: 38, height: 38, borderRadius: 9, background: H.track, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Glyph d={G.person} size={16} color={H.sub} />
          </span>
          <span
            data-testid="share-general-access"
            title="Only people you invite can open this project. Access is team-wide: an invited person sees every project in the team."
            style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 15, fontWeight: 600 }}
          >
            Only people invited
            <Glyph d={G.caret} size={12} color={H.muted} width={2} />
          </span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 18, padding: "14px 18px 16px", borderTop: `1px solid ${H.edge}` }}>
        <button
          type="button"
          className="press"
          data-testid="share-copy-link"
          onClick={async () => {
            if (await copyToClipboard(shareUrl)) setCopied(true);
          }}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 40, padding: "0 14px", borderRadius: 9, border: `1px solid ${H.edgeStrong}`, background: H.white, font: "inherit", fontSize: 14, fontWeight: 700, color: H.ink, cursor: "pointer", whiteSpace: "nowrap" }}
        >
          <Glyph d={copied ? G.check : G.send} size={13} color={copied ? H.green : H.ink} />
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>

    </div>,
    document.body,
  );
}
